import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PageArea, PageProps } from '..'
import { BulkSheetView } from '../../containers/BulkSheetView'
import { pageComponent } from '../../higher-order-components/pageComponent'
import { projectClosingStatusGridOptions } from './gridOptions'
import ProjectClosingStatusHeader from './Header'
import { useProjectClosingStatusData } from './hooks/projectClosingStatusData'
import { usePageState } from './hooks/pageState'
import { useSearchCondition } from './hooks/useSearchCondition'
import DateVO from '../../../vo/DateVO'
import { fetchProjectClosingStatusCount } from './projectClosingStatus'
import {
  ColDef,
  ColumnState,
  FilterChangedEvent,
  SortChangedEvent,
} from 'ag-grid-community'
import { SortedColumnState } from '../../model/bulkSheetColumnSortState'
import {
  useBulkSheetState,
  RestoreType,
} from '../../containers/BulkSheetView/hooks/bulkSheetState/bulkSheetState'
import { UiStateKey } from '../../../lib/commons/uiStates'
import { useColumnSetting } from '../../containers/BulkSheetView/components/columnSelector/useColumnSetting'
import ColumnSettingPopper from '../../containers/BulkSheetView/components/columnSelector/ColumnSettingPopper'
import SavedUIStateDialog from '../../components/dialogs/SavedUIStateDialog'
import MultiSelectDialog from '../../components/dialogs/MultiSelectDialog'
import { useDialog } from './hooks/dialog'
import { useExcel } from './hooks/excel'
import { intl } from '../../../i18n'
import _ from 'lodash'

type Props = PageProps

const ProjectClosingStatus = (props: Props) => {
  const functionUuid = props.uuid
  const gridOptions = useMemo(() => {
    return projectClosingStatusGridOptions()
  }, [])
  const { data, fetchRecords } = useProjectClosingStatusData()
  const ref = useRef<HTMLDivElement>(null)
  const pageState = usePageState(functionUuid)
  const [projectCount, setProjectCount] = useState<number>(0)
  const { startDate, endDate } = pageState.displayDateTerm
  const { searchCondition, onChange: onSearchConditionChange } =
    useSearchCondition()
  const [filteredColumns, setFilteredColumns] = useState<ColDef[]>([])
  const [sortColumnsState, setSortColumnsState] = useState<SortedColumnState[]>(
    []
  )
  const [isStateRestored, setIsStateRestored] = useState<boolean>(false)
  const restoreTypes = useMemo<RestoreType[]>(
    () => ['COLUMN_STATE', 'FILTER_STATE'],
    []
  )
  const projectUuid = useMemo(() => {
    return undefined
  }, [])

  const bulkSheetState = useBulkSheetState(
    functionUuid,
    'ProjectClosingStatus',
    UiStateKey.BulkSheetUIStateColumnAndFilter,
    restoreTypes,
    projectUuid
  )

  const columnSetting = useColumnSetting()
  const dialogState = useDialog()
  const { dialogOpen, openDialog, closeDialog, onExportExcel } =
    useExcel(gridOptions)

  const refresh = useCallback(async () => {
    if (!pageState.initialized) return
    const start = new DateVO(startDate).formatForApi()
    const end = new DateVO(endDate).formatForApi()
    await fetchRecords(start!, end!, searchCondition)
  }, [pageState.initialized, startDate, endDate, fetchRecords, searchCondition])

  const fetchProjectCount = useCallback(async () => {
    if (!pageState.initialized) return
    const date = new DateVO(startDate).formatForApi()!
    const count = await fetchProjectClosingStatusCount(date, searchCondition)
    setProjectCount(count.json as number)
  }, [pageState.initialized, searchCondition, setProjectCount, startDate])

  useEffect(() => {
    if (!pageState.initialized) return
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, pageState.initialized])

  useEffect(() => {
    if (!pageState.initialized) return
    fetchProjectCount()
  }, [fetchProjectCount, pageState.initialized])

  const onFilterChanged = useCallback(
    (e: FilterChangedEvent) => {
      const filterState = e.api.getFilterModel()
      bulkSheetState.saveFilterState(filterState)
      setFilteredColumns(
        Object.keys(filterState)
          .map(col => e.api.getColumnDef(col))
          .filter(v => !!v)
      )
    },
    [bulkSheetState]
  )

  const onDeleteFilteredColumn = useCallback(
    (column: ColDef) => {
      if (!column || !gridOptions.api) return
      const filterModel = gridOptions.api.getFilterModel()
      const colKey = column.colId || column.field || ''
      delete filterModel[colKey]
      gridOptions.api.setFilterModel(filterModel)

      setFilteredColumns(prev =>
        prev.filter(col => (col.colId || col.field || '') !== colKey)
      )
      bulkSheetState.saveFilterState(filterModel)
    },
    [gridOptions.api, bulkSheetState]
  )

  const resetFilters = useCallback(() => {
    if (!gridOptions.api) return
    gridOptions.api.setFilterModel(null)

    setFilteredColumns([])
    bulkSheetState.saveFilterState({})
  }, [gridOptions.api, bulkSheetState])

  const rememberColumnState = useCallback(() => {
    const columnState = gridOptions.columnApi?.getColumnState()
    columnState && bulkSheetState.saveColumnState(columnState)
  }, [bulkSheetState, gridOptions.columnApi])

  const onColumnResized = useCallback(() => {
    rememberColumnState()
  }, [rememberColumnState])

  const onColumnMoved = useCallback(() => {
    rememberColumnState()
  }, [rememberColumnState])

  const onSortedColumnsStateChanged = useCallback(
    (e: SortChangedEvent) => {
      const sortedColumns = e.columnApi
        .getColumnState()
        .filter(col => !!col.sort)
        .map(col => {
          return {
            ...e.api.getColumnDef(col.colId),
            colId: col.colId,
          }
        })
        .filter(v => !!v) as ColDef[]

      const columnState = gridOptions.columnApi?.getColumnState()
      const sortedColumnState: { [colId: string]: ColumnState } = {}
      columnState &&
        columnState.forEach(state => {
          if (state.sort) {
            sortedColumnState[state.colId] = state
          }
        })

      const sortedColumnsState: SortedColumnState[] = sortedColumns.map(col => {
        return {
          colId: col.colId,
          field: col.field,
          headerName: col.headerName,
          sort: col.colId ? sortedColumnState[col.colId]?.sort : null,
        }
      })

      setSortColumnsState(sortedColumnsState)
      bulkSheetState.saveColumnState(columnState || [])
    },
    [bulkSheetState, gridOptions.columnApi]
  )

  const onSortChanged = useCallback(
    (e: SortChangedEvent) => {
      if (e.api && e.columnApi) {
        rememberColumnState()
        onSortedColumnsStateChanged(e)
      }
    },
    [rememberColumnState, onSortedColumnsStateChanged]
  )

  const onDeleteSortedColumn = useCallback(
    (colId: string | ColDef<any>) => {
      if (!gridOptions.columnApi) return
      const colIdStr = colId.toString()
      gridOptions.columnApi.applyColumnState({
        state: [{ colId: colIdStr, sort: null }],
      })

      setSortColumnsState(prev => prev.filter(col => col.colId !== colIdStr))

      const columnState = gridOptions.columnApi.getColumnState()
      bulkSheetState.saveColumnState(columnState)
    },
    [gridOptions.columnApi, bulkSheetState]
  )

  const onDeleteSortedAllColumns = useCallback(() => {
    if (!gridOptions.columnApi) return
    gridOptions.columnApi.applyColumnState({
      defaultState: { sort: null },
    })

    setSortColumnsState([])

    const columnState = gridOptions.columnApi.getColumnState()
    bulkSheetState.saveColumnState(columnState)
  }, [gridOptions.columnApi, bulkSheetState])

  const onChangeSortColumnState = useCallback(
    (colId: string | ColDef<any>, sort: 'asc' | 'desc' | null) => {
      if (!gridOptions.columnApi) return
      gridOptions.columnApi.applyColumnState({
        state: [{ colId: colId.toString(), sort }],
      })
    },
    [gridOptions.columnApi]
  )

  const onGridReady = useCallback(
    params => {
      gridOptions.api = params.api
      gridOptions.columnApi = params.columnApi
      setIsStateRestored(true)
    },
    [gridOptions]
  )

  useEffect(() => {
    if (
      gridOptions.columnApi &&
      bulkSheetState.restored &&
      !_.isEmpty(bulkSheetState.columnState)
    ) {
      gridOptions.columnApi.applyColumnState({
        state: bulkSheetState.columnState,
        applyOrder: true,
      })
    }
  }, [
    gridOptions.columnApi,
    bulkSheetState.restored,
    bulkSheetState.columnState,
  ])

  useEffect(() => {
    if (data.length > 0 && gridOptions.api) {
      setTimeout(() => {
        gridOptions.api?.refreshCells({
          columns: ['rowNumber'],
          force: true,
        })
      }, 100)
    }
  }, [data, gridOptions.api])

  const onFirstDataRendered = useCallback(() => {
    if (!_.isEmpty(bulkSheetState.filterState)) {
      gridOptions.api?.setFilterModel(bulkSheetState.filterState)
    }
    bulkSheetState.finishRestoring()
  }, [bulkSheetState, gridOptions.api])

  const getCurrentUIState = useCallback(() => {
    return {
      columnState: bulkSheetState.columnState,
      filterState: bulkSheetState.filterState,
    }
  }, [bulkSheetState.columnState, bulkSheetState.filterState])

  const applyUIState = useCallback(
    (uiState: any) => {
      if (uiState.columnState) {
        bulkSheetState.saveColumnState(uiState.columnState)
        if (!_.isEmpty(uiState.columnState) && gridOptions.columnApi) {
          gridOptions.columnApi.applyColumnState({
            state: uiState.columnState,
            applyOrder: true,
          })
        }
      }
      if (uiState.filterState) {
        bulkSheetState.saveFilterState(uiState.filterState)
        if (!_.isEmpty(uiState.filterState) && gridOptions.api) {
          gridOptions.api.setFilterModel(uiState.filterState)
        }
      }
      if (uiState.filterState && Object.keys(uiState.filterState).length > 0) {
        const filterKeys = Object.keys(uiState.filterState)
        const filtered =
          (gridOptions.columnDefs as ColDef[])?.filter(col =>
            filterKeys.includes(col.colId || col.field || '')
          ) || []
        setFilteredColumns(filtered)
      } else {
        setFilteredColumns([])
      }

      if (uiState.columnState && uiState.columnState.length > 0) {
        const sortedColumnsState: SortedColumnState[] = uiState.columnState
          .filter(col => col.sort)
          .map(col => ({
            colId: col.colId,
            sort: col.sort!,
          }))
        setSortColumnsState(sortedColumnsState)
      } else {
        setSortColumnsState([])
      }
    },
    [
      bulkSheetState,
      gridOptions.columnApi,
      gridOptions.api,
      gridOptions.columnDefs,
    ]
  )

  useEffect(() => {
    if (
      bulkSheetState.initialized &&
      bulkSheetState.restored &&
      !isStateRestored
    ) {
      if (
        bulkSheetState.filterState &&
        Object.keys(bulkSheetState.filterState).length > 0
      ) {
        const filterKeys = Object.keys(bulkSheetState.filterState)
        const filtered =
          (gridOptions.columnDefs as ColDef[])?.filter(col =>
            filterKeys.includes(col.colId || col.field || '')
          ) || []
        setFilteredColumns(filtered)
      }

      if (bulkSheetState.columnState.length > 0) {
        const sortedColumnsState: SortedColumnState[] =
          bulkSheetState.columnState
            .filter(col => col.sort)
            .map(col => ({
              colId: col.colId,
              sort: col.sort!,
            }))
        setSortColumnsState(sortedColumnsState)
      }
    }
  }, [
    bulkSheetState.initialized,
    bulkSheetState.restored,
    bulkSheetState.filterState,
    bulkSheetState.columnState,
    gridOptions.columnDefs,
    isStateRestored,
  ])

  useEffect(() => {
    gridOptions.api?.resetRowHeights()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageState.rowHeight])

  return (
    <PageArea>
      <ProjectClosingStatusHeader
        loading={false}
        refresh={refresh}
        onMonthChange={pageState.handleMonthChange}
        searchCondition={searchCondition}
        onSearchConditionChange={onSearchConditionChange}
        onSearch={refresh}
        projectCount={projectCount}
        onClickExport={openDialog}
        filteredColumns={filteredColumns}
        onDeleteFilteredColumn={onDeleteFilteredColumn}
        resetFilters={resetFilters}
        sortColumnsState={sortColumnsState}
        onDeleteSortedColumn={onDeleteSortedColumn}
        onDeleteSortedAllColumns={onDeleteSortedAllColumns}
        onChangeSortColumnState={onChangeSortColumnState}
        onClickColumnSettingButton={columnSetting.toggle}
        columnSettingOpen={columnSetting.isOpen}
        rowHeight={pageState.rowHeight}
        onChangeRowHeight={(value: number) => {
          pageState.setRowHeight(value)
        }}
      />
      <BulkSheetView
        ref={ref}
        rowData={data}
        {...gridOptions}
        onGridReady={onGridReady}
        onFirstDataRendered={onFirstDataRendered}
        onFilterChanged={onFilterChanged}
        onSortChanged={onSortChanged}
        onColumnVisible={rememberColumnState}
        onColumnResized={onColumnResized}
        onColumnMoved={onColumnMoved}
        rowHeight={pageState.rowHeight}
      />
      <ColumnSettingPopper
        open={columnSetting.isOpen}
        anchorEl={columnSetting.anchorEl}
        close={columnSetting.close}
        columnApi={gridOptions.columnApi ?? undefined}
        gridApi={gridOptions.api ?? undefined}
        height={ref.current?.offsetHeight}
        openSavedUiStateDialog={dialogState.openUiState}
        initializeColumnState={() => {
          gridOptions.columnApi?.resetColumnState()
          gridOptions.api?.setFilterModel(null)
          bulkSheetState.saveColumnState([])
          bulkSheetState.saveFilterState({})
          setFilteredColumns([])
          setSortColumnsState([])
        }}
        applicationFunctionUuid={props.uuid}
        uiStateKey={UiStateKey.BulkSheetUIStateColumnAndFilter}
        columnState={bulkSheetState.columnState}
        offset={90}
      />
      <SavedUIStateDialog
        open={dialogState.uiState}
        onClose={dialogState.closeUiState}
        onSelect={selectedUIState => {
          const uiStateToApply = selectedUIState.UIState || selectedUIState
          applyUIState(uiStateToApply)
          dialogState.closeUiState()
        }}
        applicationFunctionUuid={props.uuid}
        uiStateKey={UiStateKey.BulkSheetUIStateColumnAndFilter}
        title={intl.formatMessage({
          id: 'bulksheet.toolbar.columnQuickFilter.label.restore',
        })}
        sharable={false}
        currentUIState={getCurrentUIState()}
      />
      {dialogOpen && (
        <MultiSelectDialog
          onClose={closeDialog}
          onSubmit={(allList, selectedItem) => {
            const selectedColIds = selectedItem.map(col => col.value)
            onExportExcel(selectedColIds)
          }}
          dialogTitle={intl.formatMessage({
            id: 'dialog.exceloutput.columnselect.title',
          })}
          submitButtonTitle={intl.formatMessage({
            id: 'dialog.exceloutput.submit',
          })}
          allCheckBoxLabel={intl.formatMessage({
            id: 'dialog.exceloutput.columnselect.allcheckboxlabel',
          })}
          getSelectList={() => {
            return (gridOptions.columnApi?.getColumns() || [])
              .filter(
                column =>
                  !['uuid', 'drag', 'action', 'select', 'rowNumber'].includes(
                    column.getColId()
                  )
              )
              .map(column => {
                return {
                  value: column.getColId(),
                  displayName:
                    gridOptions.columnApi?.getDisplayNameForColumn(
                      column,
                      null
                    ) ?? '',
                  defaultChecked:
                    column.isVisible() ||
                    column.getColDef().cellRendererParams?.uiMeta?.tree,
                }
              })
          }}
          hideIcon={true}
        />
      )}
    </PageArea>
  )
}

export default pageComponent(ProjectClosingStatus)
